import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Checkbox, Group, NumberInput, SegmentedControl, Stack, Text } from '@mantine/core';
import { Building2, KeyRound, Trash2, UserPlus, Users } from 'lucide-react';
import { Badge, Button, Card, ConfirmDialog, PageContainer, PageHeader, Section, Table, type TableColumn } from '../components/ui';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { useState } from 'react';
import { useDeleteTenant, useSetLicense, useTenant, useUsers, type AdminUser } from '../hooks/useAdmin';
import { useSetOrgTemplate, type ExperienceTemplate } from '../hooks/useExperience';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { toast } from '../store/useToasts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/tenants_/$tenantId')({
  component: TenantDetail,
});

function TenantDetail() {
  const { tenantId } = Route.useParams() as { tenantId: string };
  const navigate = useNavigate();
  const role = useAuthStore(selectRole);
  const isSuper = role === 'superadmin';
  const tenant = useTenant(tenantId);
  const users = useUsers(tenantId);
  const del = useDeleteTenant();
  const setLicense = useSetLicense();
  const [confirm, setConfirm] = useState(false);
  const [licMonths, setLicMonths] = useState<number | ''>(12);
  const [scenOn, setScenOn] = useState(true);
  const [licInit, setLicInit] = useState(false);
  const setTemplate = useSetOrgTemplate();
  const [tmpl, setTmpl] = useState<ExperienceTemplate>('default');
  // Seed the scenarios toggle + experience template from the loaded org once.
  if (!licInit && tenant.data) {
    setScenOn(tenant.data.scenariosEnabled ?? true);
    setTmpl(tenant.data.template ?? 'default');
    setLicInit(true);
  }

  // The trainee experience template auto-saves the instant it changes.
  const changeTemplate = (next: ExperienceTemplate) => {
    const prev = tmpl;
    setTmpl(next);
    setTemplate.mutate(
      { tenantId, template: next },
      {
        onSuccess: () => { toast.success(next === 'fortress' ? 'Experience set to Fortress' : 'Experience set to Default'); void tenant.refetch(); },
        onError: (e) => { setTmpl(prev); toast.error(e.message || 'Could not change the experience'); },
      },
    );
  };

  const fmt = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };
  // Renew / change the license duration (explicit button). Sends the current
  // scenarios state too; the backend preserves anything a request omits.
  const saveLicense = () => {
    setLicense.mutate(
      { tenantId, scenariosEnabled: scenOn, ...(licMonths === '' ? {} : { licenseMonths: Number(licMonths) }) },
      { onSuccess: () => { toast.success('License updated'); void tenant.refetch(); },
        onError: (e) => toast.error(e.message || 'Could not update the license') },
    );
  };

  // Scenarios entitlement is a live toggle — persist the instant it flips so it
  // can't silently revert because a separate "save" button was never clicked.
  // No licenseMonths is sent, so the backend keeps the existing expiry.
  const toggleScenarios = (next: boolean) => {
    setScenOn(next); // optimistic
    setLicense.mutate(
      { tenantId, scenariosEnabled: next },
      {
        onSuccess: () => { toast.success(next ? 'Scenarios enabled' : 'Scenarios disabled'); void tenant.refetch(); },
        onError: (e) => { setScenOn(!next); toast.error(e.message || 'Could not update scenarios access'); },
      },
    );
  };

  const userCols: ReadonlyArray<TableColumn<AdminUser>> = [
    { key: 'email', header: 'Email', cell: (u) => <Text size="sm" className="font-mono" c="rgb(var(--c-txt-bright))">{u.email}</Text> },
    { key: 'role', header: 'Role', cell: (u) => <Badge variant={u.role === 'org_admin' ? 'warn' : 'info'} size="xs">{u.role}</Badge> },
    { key: 'mfa', header: 'MFA', cell: (u) => <Badge variant={u.mfaEnabled ? 'success' : 'danger'} size="xs">{u.mfaEnabled ? 'on' : 'off'}</Badge> },
    { key: 'status', header: 'Status', cell: (u) => <Badge variant={u.status === 'active' ? 'success' : u.status === 'invited' ? 'info' : 'danger'} size="xs">{u.status}</Badge> },
  ];

  const removeTenant = () => {
    del.mutate(
      { tenantId },
      {
        onSuccess: () => { toast.success('Organization removed'); void navigate({ to: '/admin/tenants' }); },
        onError: (e) => toast.error(e.message || 'Could not remove the organization'),
      },
    );
  };

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Building2}
        title={tenant.data?.name ?? 'Organization'}
        description={tenant.data ? `Slug: ${tenant.data.slug ?? '—'} · ${tenant.data.users} user${tenant.data.users === 1 ? '' : 's'} · cap $${tenant.data.spendCap}` : ''}
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Tenants', to: '/admin/tenants' }, { label: tenant.data?.name ?? '…' }]}
        actions={
          <Group gap="xs">
            <Button variant="secondary" leftIcon={<UserPlus size={14} />} onClick={() => void navigate({ to: '/admin/users' })}>
              Add user
            </Button>
            {isSuper && tenant.data && !tenant.data.isPlatform ? (
              <Button variant="ghost" leftIcon={<Trash2 size={14} />} onClick={() => setConfirm(true)}>
                Remove
              </Button>
            ) : null}
          </Group>
        }
      />

      {tenant.isError ? (
        <ErrorState title="Could not load the organization" message={(tenant.error as Error)?.message ?? ''} onRetry={() => void tenant.refetch()} />
      ) : tenant.isPending ? (
        <LoadingState rows={2} height={40} />
      ) : (
        <Stack gap="lg">
          <Group gap="lg" wrap="wrap">
            {[
              ['Status', tenant.data?.status ?? '—'],
              ['Users', String(tenant.data?.users ?? 0)],
              ['Spend cap', `$${tenant.data?.spendCap ?? 0}`],
              ['MTD spend', `$${(tenant.data?.monthToDate ?? 0).toFixed(2)}`],
            ].map(([label, value]) => (
              <Card key={label}>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
                  <Text size="lg" fw={700}>{value}</Text>
                </Stack>
              </Card>
            ))}
          </Group>

          {isSuper ? (
            <Section title="License & Entitlements" description="Set how long this org can use the platform, and whether scenarios are enabled.">
              <Card>
                <Stack gap="md">
                  <Group gap="lg" wrap="wrap">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>License window</Text>
                      <Text size="sm" fw={600} c={tenant.data?.licenseValid === false ? 'red' : 'rgb(var(--c-txt-bright))'}>
                        {fmt(tenant.data?.licenseStartedAt)} \u2192 {fmt(tenant.data?.licenseExpiresAt)}
                        {tenant.data?.licenseValid === false ? ' (expired)' : ''}
                      </Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Status</Text>
                      <Badge variant={tenant.data?.licenseValid === false ? 'danger' : 'success'} size="sm">
                        {tenant.data?.licenseValid === false ? 'Expired' : 'Valid'}
                      </Badge>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Scenarios</Text>
                      <Badge variant={tenant.data?.scenariosEnabled ? 'success' : 'warn'} size="sm">
                        {tenant.data?.scenariosEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </Stack>
                  </Group>
                  <Group gap="md" align="flex-end" wrap="wrap">
                    <Checkbox label="Scenarios enabled (commercial add-on)" checked={scenOn}
                      description="Saves instantly. When off, the org sees the Scenarios tab but cannot deploy."
                      disabled={setLicense.isPending}
                      onChange={(e) => toggleScenarios(e.currentTarget.checked)} />
                    <NumberInput label="Renew / set duration (months from now)" value={licMonths} min={1} max={120}
                      onChange={(v) => setLicMonths(typeof v === 'number' ? v : '')} style={{ width: 260 }} />
                    <Button variant="primary" leftIcon={<KeyRound size={14} />} loading={setLicense.isPending} onClick={saveLicense}>
                      Renew license
                    </Button>
                  </Group>
                  <Stack gap={6} mt="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Trainee experience</Text>
                    <Text size="xs" c="dimmed">Which UI this org&rsquo;s trainees see. Fortress is the gamified operator console; saves instantly.</Text>
                    <SegmentedControl
                      value={tmpl}
                      onChange={(v) => changeTemplate(v as ExperienceTemplate)}
                      disabled={setTemplate.isPending}
                      data={[{ label: 'Default', value: 'default' }, { label: 'Fortress', value: 'fortress' }]}
                      style={{ width: 'fit-content' }}
                    />
                  </Stack>
                </Stack>
              </Card>
            </Section>
          ) : null}

          <Section title="Users" description="Everyone in this organization.">
            {users.isPending ? (
              <LoadingState rows={3} height={30} />
            ) : (users.data?.length ?? 0) === 0 ? (
              <Group gap="xs"><Users size={16} /><Text size="sm" c="dimmed">No users in this organization yet.</Text></Group>
            ) : (
              <Card>
                <Table columns={userCols} rows={users.data ?? []} rowKey={(u) => u.id} />
              </Card>
            )}
          </Section>
        </Stack>
      )}

      <ConfirmDialog
        opened={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={() => { setConfirm(false); removeTenant(); }}
        title={`Remove ${tenant.data?.name ?? 'this organization'}?`}
        confirmLabel="Remove organization"
        loading={del.isPending}
      >
        {`This suspends the organization and disables its users. It disappears from the tenants list. This does not delete lab history.`}
      </ConfirmDialog>
    </PageContainer>
  );
}
